import React, { useState, useEffect, useCallback, useRef } from 'react';
import { orderStatusLabel } from '../orderStatus';
import { api } from '../api';
import { nfConfirm } from '../components/confirm';
import { formatPhoneDisplay, phoneTelHref } from '../utils';
import { openRoute, openAddress, orderPoint } from '../navLinks';
import useCourierGeolocation from '../hooks/useCourierGeolocation';
import { openOrdersStream } from '../utils/ordersStream';

// Иконки вместо эмодзи (у эмодзи вид зависит от телефона): стрелка навигации и часы
const NavIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" strokeLinejoin="miter" style={{ marginRight: 6, verticalAlign: '-2px' }}>
    <path d="M21 3L3 10l8 3 3 8 7-18z" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" strokeLinejoin="miter" style={{ marginRight: 5, verticalAlign: '-2px' }}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

// Доля курьера — целыми рублями, как во всей админке (было «299.00 ₽»)
const rubles = (v) => `${Math.round(Number(v) || 0).toLocaleString('ru-RU')} ₽`;
// Имя заказчика: из профиля, у заказа без профиля («Трафик», Flowwow) — из заказа, а не «Клиент»
const customerName = (o) => [o.user?.first_name, o.user?.last_name].filter(Boolean).join(' ') || o.customer_name || 'Клиент';

function CourierDeliveriesPage({ showToast }) {
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [dates, setDates] = useState([]);
  const [ordersGrouped, setOrdersGrouped] = useState({ courier: [], completed: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const loadOrdersRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);
  // Геолокация: разовое согласие (152-ФЗ) хранится на сервере; после него позиция
  // передаётся автоматически, пока открыта эта страница. Кнопок «начать/завершить» нет.
  const [consented, setConsented] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  // Навигатор не открылся (нет приложения / отменили) — ссылка на веб-карты, вкладку не уводим
  const [naviFallback, setNaviFallback] = useState(null);
  const navi = (points) => {
    setNaviFallback(null);
    openRoute(points, { onFallback: (web) => setNaviFallback(web) });
  };
  const geoError = useCallback((kind) => {
    const msgs = {
      consent: 'Нужно согласие на передачу геопозиции',
      denied: 'Доступ к геопозиции запрещён — разрешите его для сайта в настройках браузера',
      unsupported: 'Браузер не поддерживает геолокацию',
    };
    if (msgs[kind]) showToast(msgs[kind], 'error');
  }, [showToast]);
  const geo = useCourierGeolocation({ onError: geoError });
  const geoStart = geo.start;

  useEffect(() => { generateDates(); }, []);

  useEffect(() => {
    api.getMyLocationState()
      .then((s) => setConsented(!!s.consented_at))
      .catch(() => setConsented(false));
  }, []);

  // Согласие есть → передаём сразу; нет → спрашиваем один раз (отказ помним до закрытия вкладки)
  useEffect(() => {
    if (consented === true) geoStart();
    else if (consented === false) {
      let declined = false;
      try { declined = sessionStorage.getItem('courier_geo_declined') === '1'; } catch { /* noop */ }
      if (!declined) setShowConsent(true);
    }
  }, [consented, geoStart]);

  const handleConsent = async () => {
    try {
      await api.setLocationConsent();
      setShowConsent(false);
      setConsented(true);
    } catch {
      showToast('Не удалось сохранить согласие', 'error');
    }
  };

  const handleDecline = () => {
    try { sessionStorage.setItem('courier_geo_declined', '1'); } catch { /* noop */ }
    setShowConsent(false);
  };

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getCourierOrders(selectedDate);
      console.log('[CourierDeliveries] Loaded orders:', data);
      // Проверяем, есть ли courier_delivery_cost в первом заказе
      if (data.courier && data.courier[0]) {
        console.log('[CourierDeliveries] First order:', data.courier[0]);
        console.log('[CourierDeliveries] courier_delivery_cost:', data.courier[0].courier_delivery_cost);
      }
      setOrdersGrouped(data);
    } catch {
      showToast('Ошибка загрузки доставок', 'error');
      setOrdersGrouped({ courier: [], completed: [] });
    } finally { setIsLoading(false); }
  }, [selectedDate, showToast]);

  useEffect(() => { if (selectedDate) loadOrders(); }, [selectedDate, loadOrders]);

  // Сохраняем ссылки на loadOrders и selectedDate
  useEffect(() => {
    loadOrdersRef.current = loadOrders;
  }, [loadOrders]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  // SSE: автообновление при назначении заказа или изменении статуса
  useEffect(() => {
    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream
    const es = openOrdersStream();
    console.log('[CourierDeliveries] SSE connection opened');
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[CourierDeliveries] SSE event:', data, 'currentSelectedDate:', selectedDateRef.current);
        if ((data.type === 'new_order' || data.type === 'order_updated') && data.delivery_date === selectedDateRef.current) {
          console.log('[CourierDeliveries] Reloading orders after SSE event');
          setTimeout(() => {
            if (loadOrdersRef.current) {
              loadOrdersRef.current();
            }
          }, 100);
        }
      } catch (e) {
        console.error('[CourierDeliveries] SSE error:', e);
      }
    };
    es.onerror = (error) => {
      console.error('[CourierDeliveries] SSE connection error:', error);
    };
    return () => {
      console.log('[CourierDeliveries] SSE connection closed');
      es.close();
    };
  }, []); // Empty dependency array - connection established once

  function getTodayDate() { return formatDateValue(new Date()); }

  function formatDateValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function formatDateDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getDate()} ${date.toLocaleDateString('ru-RU', { month: 'short' })}`;
  }

  function getDayName(dateStr) {
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[new Date(dateStr + 'T00:00:00').getDay()];
  }

  const getStatusLabel = orderStatusLabel; // единые подписи (src/orderStatus.js)

  function getStatusMeta(status) {
    const map = {
      new:        { cls: 'new',        icon: '/icons/nv-status-new.svg' },
      packaging:  { cls: 'packaging',  icon: '/icons/nv-package.svg' },
      ready:      { cls: 'ready',      icon: '/icons/nv-status-ready.svg' },
      courier:    { cls: 'courier',    icon: '/icons/nv-delivery.svg' },
      completed:  { cls: 'completed',  icon: '/icons/nv-status-completed.svg' },
    };
    return map[status] || { cls: 'new', icon: null };
  }

  function generateDates() {
    const today = new Date();
    const result = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      result.push(formatDateValue(d));
    }
    setDates(result);
  }

  // «Доставлен» — с подтверждением: случайное касание в дороге закрывало заказ (владелец 25.10)
  const handleMarkDelivered = async (order) => {
    // номер — как в карточке выше: у доставки подписки «подписка/доставка»
    const num = order.order_type === 'subscription'
      ? `${order.subscription_order_number || order.subscription_id}/${order.subscription_delivery_number}`
      : (order.order_number || order.id);
    if (!(await nfConfirm(`Вы действительно доставили заказ #${num}?`, { confirmText: 'Да', cancelText: 'Нет' }))) return;
    try { await api.updateCourierOrder(order.id, { status: 'completed' }); showToast('Доставлен'); loadOrders(); }
    catch { showToast('Ошибка', 'error'); }
  };

  const activeOrders = ordersGrouped.courier || [];
  const doneOrders = ordersGrouped.completed || [];
  const displayOrders = activeTab === 'active' ? activeOrders : doneOrders;
  const todayDate = getTodayDate();
  const totalOrders = activeOrders.length + doneOrders.length;
  // Весь маршрут по незакрытым точкам в порядке объезда — deep-link в Навигатор.
  // Два заказа в один дом в ОДНОМ слоте — один визит, одна точка. В разных слотах —
  // два визита, обе точки остаются (Навигатор рисует их одна поверх другой — это
  // особенность карты, подсказка «ещё по этому адресу» в карточке объясняет)
  const pointKey = (p) => `${p[0].toFixed(5)},${p[1].toFixed(5)}`;
  const visitKey = (o) => `${pointKey(orderPoint(o))}|${o.delivery_time || ''}`;
  const routePoints = activeOrders.filter(orderPoint)
    .filter((o, i, arr) => arr.findIndex((q) => visitKey(q) === visitKey(o)) === i)
    .map(orderPoint);
  // Другие незакрытые заказы по тому же адресу — подсказка в карточке (со слотом)
  const sameAddress = (order) => {
    const p = orderPoint(order);
    if (!p) return [];
    return activeOrders.filter((o) => o.id !== order.id && orderPoint(o) && pointKey(orderPoint(o)) === pointKey(p));
  };

  // Рассчитываем заработок курьера за день (только доставленные заказы)
  const totalEarnings = doneOrders.reduce((sum, order) => {
    return sum + (order.courier_delivery_cost || 0);
  }, 0);

  return (
    <div>
      {/* 3-day navigation — бренд */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12, padding: '10px 0', borderBottom: '1px solid #EBEDEC' }}>
        {dates.map(date => {
          const isToday = date === todayDate;
          const isSelected = date === selectedDate;
          return (
            <button key={date} onClick={() => setSelectedDate(date)} style={{
              minWidth: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '8px 14px', '--ch': '8px', '--stroke': (isSelected ? '#1C3D2E' : (isToday ? '#B0603C' : '#D8DDDA')),
              border: isSelected ? '1px solid #1C3D2E' : (isToday ? '1px solid #B0603C' : '1px solid #D8DDDA'),
              background: isSelected ? '#1C3D2E' : '#fff',
              color: isSelected ? '#fff' : '#1C3D2E',
              fontWeight: isToday || isSelected ? 600 : 500,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s, --stroke 0.15s',
            }}>
              <span style={{ fontSize: 11, opacity: 0.85 }}>{getDayName(date)}</span>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{formatDateDisplay(date)}</span>
            </button>
          );
        })}
      </div>

      {/* Info bar — primary green */}
      <div style={{
        background: '#1C3D2E',
        color: '#fff', padding: '10px 16px', '--ch': '8px', marginBottom: 12,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
      }}>
        <span style={{ fontSize: 13, opacity: 0.85 }}>{getDayName(selectedDate)}, {formatDateDisplay(selectedDate)}</span>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {geo.supported && consented === false && (
            <button onClick={() => setShowConsent(true)} style={routeBtnStyle} title="Показывать вашу позицию диспетчеру, пока открыта эта страница">
              📍 Разрешить геопозицию
            </button>
          )}
          {routePoints.length > 0 && (
            <button onClick={() => navi(routePoints)} style={routeBtnStyle} title="Открыть весь маршрут в Яндекс.Навигаторе">
              <NavIcon />Яндекс · весь маршрут ({routePoints.length})
            </button>
          )}
          <span style={{ fontSize: 13, fontWeight: 700, padding: '4px 10px', background: '#FFE7DD', color: '#B0603C', '--ch': '5px'}}>
            Итог: {rubles(totalEarnings)}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700 }}>
            {totalOrders} {totalOrders === 1 ? 'доставка' : totalOrders > 4 ? 'доставок' : 'доставки'}
          </span>
        </div>
      </div>

      {/* Статус передачи позиции */}
      {naviFallback && (
        <div style={{ fontSize: 13, color: '#1C3D2E', marginBottom: 10, padding: '8px 10px', background: '#FFE7DD', '--ch': '5px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Навигатор не открылся?</span>
          <a href={naviFallback} target="_blank" rel="noreferrer" onClick={() => setNaviFallback(null)} style={{ color: '#1C3D2E', fontWeight: 700 }}>
            Открыть в Яндекс Картах
          </a>
          <button onClick={() => setNaviFallback(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#5E6561', cursor: 'pointer', fontSize: 16, lineHeight: 1 }} aria-label="Закрыть">×</button>
        </div>
      )}

      {/* Передача позиции идёт молча; показываем только проблемы */}
      {geo.active && geo.error && (
        <div style={{ fontSize: 12, color: '#B0603C', marginBottom: 10, padding: '6px 10px', background: '#F4F4EF', '--ch': '5px'}}>
          {geo.error === 'denied' && 'Геопозиция запрещена для сайта — разрешите её в настройках браузера, иначе диспетчер вас не видит'}
          {geo.error === 'position' && 'Не удаётся определить позицию — подождите или выйдите на открытое место'}
          {geo.error === 'send' && 'Позиция не отправляется — нет связи с сервером, повторим автоматически'}
        </div>
      )}

      {/* Согласие на передачу геопозиции */}
      {showConsent && (
        <div style={overlayStyle} onClick={handleDecline}>
          <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1C3D2E', marginBottom: 10 }}>Геопозиция для диспетчера</div>
            <div style={{ fontSize: 13, color: '#1C3D2E', lineHeight: 1.45, marginBottom: 14 }}>
              Пока открыта эта страница, диспетчер видит вас на карте и точнее считает время
              прибытия. Хранится только последняя точка, без истории; через сутки она удаляется.
              Разрешение даётся один раз — браузер тоже спросит один раз.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={handleDecline} className="btn btn-secondary btn-md">Не сейчас</button>
              <button type="button" onClick={handleConsent} className="btn btn-primary btn-md">Разрешить</button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs — бренд */}
      <div className="seg staff-tabs" style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => setActiveTab('active')} className={`seg-btn${activeTab === 'active' ? ' on' : ''}`}>
          К доставке ({activeOrders.length})
        </button>
        <button type="button" onClick={() => setActiveTab('done')} className={`seg-btn${activeTab === 'done' ? ' on' : ''}`}>
          Доставлено ({doneOrders.length})
        </button>
      </div>

      {/* Orders */}
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : displayOrders.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#5E6561', padding: '32px 0' }}>
          {activeTab === 'active' ? 'Нет доставок на этот день' : 'Нет доставленных заказов'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {displayOrders.map((order) => {
            const meta = getStatusMeta(order.status);
            return (
            <div key={order.id} style={cardStyle}>
              {/* Шапка карточки */}
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {order.delivery_sequence && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: '50%',
                    background: '#1C3D2E', color: '#fff', fontSize: 14, fontWeight: 700
                  }}>
                    {order.delivery_sequence}
                  </span>
                )}
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1C3D2E' }}>
                  #{order.order_type === 'subscription'
                    ? `${order.subscription_order_number || order.subscription_id}/${order.subscription_delivery_number}`
                    : (order.order_number || order.id)}
                  {order.source_platform === 'flowwow' && order.external_platform_order_id && <span style={{ fontWeight: 500, color: '#C2185B' }}> / {order.external_platform_order_id}</span>}
                  {order.order_type === 'subscription' && (
                    <img src="/icons/nv-stats-subscriptions.svg" alt="" style={{ width: 14, height: 14, verticalAlign: '-2px', marginLeft: 4 }} />
                  )}
                </span>
                <span style={{ color: '#5E6561', fontSize: 13, fontWeight: 600 }}>· {order.delivery_time || '—'}</span>
                {(order.deliver_from || order.deliver_to) && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', background: '#F9EBCF', color: '#6D4C41', '--ch': '5px'}}
                        title="Просьба клиента">
                    <ClockIcon />{order.deliver_from ? `не раньше ${order.deliver_from}` : ''}{order.deliver_from && order.deliver_to ? ', ' : ''}{order.deliver_to ? `не позже ${order.deliver_to}` : ''}
                  </span>
                )}
                {order.delivery_type !== 'pickup' && (order.courier_delivery_cost > 0) && (
                  <span style={{
                    marginLeft: 'auto', fontSize: 13, fontWeight: 700,
                    padding: '3px 10px', background: '#DFF2E8', color: '#1C3D2E', '--ch': '5px',
                  }}>
                    {rubles(order.courier_delivery_cost)}
                  </span>
                )}
              </div>

              {/* Получатель + Клиент */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ fontSize: 13, color: '#1C3D2E' }}>
                  {order.is_recipient_other ? (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 3, color: '#B0603C', fontSize: 13 }}>
                        Получатель: {order.recipient_name}
                      </div>
                      <div style={{ fontSize: 12, color: '#5E6561' }}>
                        <a href={phoneTelHref(order.recipient_phone)} style={{ color: '#1C3D2E', textDecoration: 'none' }}>
                          {formatPhoneDisplay(order.recipient_phone)}
                        </a>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
                        {customerName(order)}
                      </div>
                      <div style={{ fontSize: 12, color: '#5E6561' }}>
                        <a href={phoneTelHref(order.phone)} style={{ color: '#1C3D2E', textDecoration: 'none' }}>
                          {formatPhoneDisplay(order.phone)}
                        </a>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ fontSize: 13, color: '#1C3D2E' }}>
                  {order.is_recipient_other && (
                    <>
                      <div style={{ fontSize: 11, color: '#5E6561', marginBottom: 2, fontWeight: 600 }}>
                        Клиент
                      </div>
                      <div style={{ fontWeight: 500, fontSize: 12 }}>
                        {customerName(order)}
                      </div>
                      <div style={{ fontSize: 11, color: '#5E6561' }}>
                        {formatPhoneDisplay(order.phone)}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Адрес — бренд-зелёный блок */}
              <div style={{ marginTop: 10, background: '#DFF2E8', borderLeft: '3px solid #1C3D2E', padding: '8px 12px', '--ch': '6px'}}>
                {order.city_district && (
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 700, color: '#1C3D2E', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {order.city_district}
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1C3D2E', marginBottom: 4 }}>
                  {order.delivery_address || 'Самовывоз'}
                </div>
                {(order.apartment || order.entrance || order.floor || order.intercom) && (
                  <div style={{ fontSize: 12, color: '#5E6561' }}>
                    {/* через « · » только между заполненными — без висящей точки после «кв. 1» */}
                    {[['кв.', order.apartment], ['под.', order.entrance], ['эт.', order.floor], ['дом.', order.intercom]]
                      .filter(([, v]) => v)
                      .map(([k, v], i) => <span key={k}>{i > 0 && ' · '}{k} <strong style={{ color: '#1C3D2E' }}>{v}</strong></span>)}
                  </div>
                )}
                {sameAddress(order).length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#7E5D00', background: '#F9EBCF', padding: '4px 8px', '--ch': '5px'}}>
                    Ещё по этому адресу: {sameAddress(order).map((o) => `#${o.order_number}${o.delivery_time ? (o.delivery_time === order.delivery_time ? ' (тот же слот)' : ` (слот ${o.delivery_time})`) : ''}`).join(', ')}
                  </div>
                )}
              </div>

              {/* Комментарий клиента для курьера */}
              {order.courier_comment && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#F4F4EF', '--ch': '5px', color: '#5E6561', fontSize: 12 }}>
                  <strong style={{ color: '#1C3D2E' }}>Комментарий клиента:</strong> {order.courier_comment}
                </div>
              )}

              {/* Action buttons */}
              {activeTab === 'active' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  <button type="button" onClick={() => handleMarkDelivered(order)} className="btn btn-primary btn-md">
                    Доставлен
                  </button>
                  {orderPoint(order) && (
                    <button
                      onClick={() => { setNaviFallback(null); openAddress(order, { onFallback: (web) => setNaviFallback(web) }); }}
                      type="button" className="btn btn-secondary btn-md"
                      title="Открыть адрес в Яндекс.Навигаторе (поиск по адресу — дом и подпись как в карточке), дальше «Поехали»"
                    >
                      <NavIcon />Яндекс
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const cardStyle = { background: '#fff', '--ch': '8px', '--stroke': '#EBEDEC', padding: 14, border: '1px solid #EBEDEC' };
const routeBtnStyle = {
  background: '#fff', color: '#1C3D2E', border: 'none', '--ch': '5px',
  padding: '4px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const dialogStyle = {
  background: '#fff', '--ch': '10px', padding: 18, maxWidth: 420, width: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};

export default CourierDeliveriesPage;
